import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";

import {
  LocalizedProperty,
  PaginateQuery,
  SyncChangeAction,
  SyncSubjectsPreview,
} from "../../interfaces";
import { EducationalStageDto } from "../../interfaces/curriculum";
import { ActivityButton } from "../../components/Common";
import { SubjectSelect } from "../../components";
import {
  usePreviewSyncSubjectsMutation,
  useSyncSubjectsMutation,
} from "../../store/api/subjectApi";
import { useGetEducationSystemSelectListQuery } from "../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../store/api/courseApi";
import { useGetEducationalStagesQuery } from "../../store/api/educationalStageApi";

type SyncSubjectFormValues = {
  sourceSubjectId: string;
  targetSubjectId: string;
  syncingToCourseId: string;
  syncingToGradeId: string;
};

type SyncStep = "select" | "diff" | "confirm";

type TProps = {
  id: string;
  show: boolean;
  onCloseClick: (event: any) => void;
  name: LocalizedProperty | null;
  defaultEducationSystemId?: string;
  defaultCurriculumId?: string;
  defaultCourseId?: string;
  defaultStageId?: string;
  defaultGradeId?: string;
};

const actionBadgeColor: Record<SyncChangeAction, string> = {
  create: "success",
  update: "warning",
  delete: "danger",
};

const labelOf = (
  item:
    | {
        value?: { en?: string; ar?: string };
        name?: { en?: string; ar?: string };
      }
    | undefined,
  langKey: "en" | "ar",
) =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  "";

const SyncDiffSummary: React.FC<{
  preview: SyncSubjectsPreview;
  localizedName: (value: LocalizedProperty) => string;
}> = ({ preview, localizedName }) => {
  const { t } = useTranslation();
  return (
    <>
      <p className="text-muted mb-2">
        {t("syncChangesSummary", {
          source: localizedName(preview.sourceSubjectName),
          target: localizedName(preview.targetSubjectName),
        })}
      </p>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Badge color="success">
          {t("syncAction_create")}: {preview.summary.create}
        </Badge>
        <Badge color="warning">
          {t("syncAction_update")}: {preview.summary.update}
        </Badge>
        <Badge color="danger">
          {t("syncAction_delete")}: {preview.summary.delete}
        </Badge>
      </div>
    </>
  );
};

const SyncDiffTable: React.FC<{
  preview: SyncSubjectsPreview;
  entityTypeLabel: (entityType: string) => string;
  actionLabel: (action: SyncChangeAction) => string;
}> = ({ preview, entityTypeLabel, actionLabel }) => {
  const { t } = useTranslation();

  if (preview.changes.length === 0) {
    return (
      <Alert color="secondary" className="mb-0">
        {t("syncNoChanges")}
      </Alert>
    );
  }

  return (
    <div
      className="table-responsive"
      style={{ maxHeight: "50vh", overflowY: "auto" }}
    >
      <Table className="table-sm align-middle mb-0">
        <thead className="table-light sticky-top">
          <tr>
            <th>{t("syncChangeAction")}</th>
            <th>{t("syncChangeType")}</th>
            <th>{t("syncChangeName")}</th>
            <th>{t("syncChangePath")}</th>
          </tr>
        </thead>
        <tbody>
          {preview.changes.map((change, index) => (
            <tr key={`${change.entityType}-${change.action}-${index}`}>
              <td>
                <Badge color={actionBadgeColor[change.action]}>
                  {actionLabel(change.action)}
                </Badge>
              </td>
              <td>{entityTypeLabel(change.entityType)}</td>
              <td className="text-break">{change.name}</td>
              <td className="text-muted small text-break">
                {change.path || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export const SyncSubjectModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  name,
  defaultEducationSystemId,
  defaultCurriculumId,
  defaultCourseId,
  defaultStageId,
  defaultGradeId,
}) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const [step, setStep] = useState<SyncStep>("select");
  const [preview, setPreview] = useState<SyncSubjectsPreview | null>(null);

  const [educationSystemId, setEducationSystemId] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [stageId, setStageId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [hierarchySeeded, setHierarchySeeded] = useState(false);

  const [previewSync, { isLoading: previewLoading, error: previewError }] =
    usePreviewSyncSubjectsMutation();
  const [syncSubjects, { isLoading: syncingSubject, error: syncingError }] =
    useSyncSubjectsMutation();

  const previewErrorMsg = previewError
    ? (previewError as any)?.data?.message || "An error occurred"
    : undefined;
  const syncingErrorMsg = syncingError
    ? (syncingError as any)?.data?.message || "An error occurred"
    : undefined;

  const { data: educationSystems } = useGetEducationSystemSelectListQuery(
    undefined,
    { skip: !show },
  );
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !show || !educationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery(undefined, {
    skip: !show,
  });

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId,
      courseId,
    };
    return q;
  }, [curriculumId, courseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !show || !curriculumId || !courseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];

  const selectedStage = useMemo(
    () => stages.find((s) => s.id === stageId),
    [stages, stageId],
  );
  const grades = selectedStage?.grades || [];

  const form = useFormik<SyncSubjectFormValues>({
    enableReinitialize: true,
    initialValues: {
      sourceSubjectId: id,
      targetSubjectId: "",
      syncingToCourseId: defaultCourseId ?? "",
      syncingToGradeId: "",
    },
    validationSchema: Yup.object({
      sourceSubjectId: Yup.string().required(),
      targetSubjectId: Yup.string().required(
        t("isNotEmpty", { displayName: "subject" }),
      ),
      syncingToCourseId: Yup.string().required(
        t("isNotEmpty", { displayName: "course" }),
      ),
      syncingToGradeId: Yup.string().required(
        t("isNotEmpty", { displayName: "grade" }),
      ),
    }),
    onSubmit: async () => {},
  });

  const resetHierarchy = () => {
    setEducationSystemId("");
    setCurriculumId("");
    setCourseId("");
    setStageId("");
    setGradeId("");
    setHierarchySeeded(false);
  };

  const onClose = () => {
    form.resetForm();
    resetHierarchy();
    setStep("select");
    setPreview(null);
    onCloseClick(!show);
  };

  const getTitle = () => {
    if (step === "diff") return t("syncDiffTitle");
    if (step === "confirm") return t("syncConfirmTitle");
    if (name?.ar && name?.en) {
      return `${t("sync")} ${i18n.language === "ar" ? name.ar : name.en}`;
    }
    return t("sync");
  };

  const localizedName = (value: LocalizedProperty) =>
    i18n.language === "ar" ? value.ar || value.en : value.en || value.ar;

  const clearTargetSubject = () => {
    form.setFieldValue("targetSubjectId", "");
  };

  const handleShowDiff = async () => {
    const errors = await form.validateForm();
    form.setTouched({
      sourceSubjectId: true,
      targetSubjectId: true,
      syncingToCourseId: true,
      syncingToGradeId: true,
    });
    if (Object.keys(errors).length > 0) return;

    try {
      const result = await previewSync({
        sourceSubjectId: form.values.sourceSubjectId,
        targetSubjectId: form.values.targetSubjectId,
      }).unwrap();
      setPreview(result);
      setStep("diff");
    } catch (err) {}
  };

  const handleExecuteSync = async () => {
    try {
      await syncSubjects({
        sourceSubjectId: form.values.sourceSubjectId,
        targetSubjectId: form.values.targetSubjectId,
      }).unwrap();
      toast.success(t("subjectSyncCompleted"), { autoClose: 3000 });
      onClose();
    } catch (err) {}
  };

  const entityTypeLabel = (entityType: string) => {
    const key = `syncEntity_${entityType}`;
    const translated = t(key);
    return translated === key ? entityType : translated;
  };

  const actionLabel = (action: SyncChangeAction) => t(`syncAction_${action}`);

  const hasChanges = (preview?.changes.length ?? 0) > 0;

  //#region hierarchy effects
  useEffect(() => {
    if (!show || hierarchySeeded) return;
    setEducationSystemId(defaultEducationSystemId || "");
    setCurriculumId(defaultCurriculumId || "");
    setCourseId(defaultCourseId || "");
    setStageId(defaultStageId || "");
    setGradeId(defaultGradeId || "");
    form.setFieldValue("syncingToCourseId", defaultCourseId || "");
    form.setFieldValue("syncingToGradeId", defaultGradeId || "");
    form.setFieldValue("targetSubjectId", "");
    setHierarchySeeded(true);
  }, [
    show,
    hierarchySeeded,
    defaultEducationSystemId,
    defaultCurriculumId,
    defaultCourseId,
    defaultStageId,
    defaultGradeId,
  ]);

  useEffect(() => {
    if (!show) {
      resetHierarchy();
      form.resetForm();
      setStep("select");
      setPreview(null);
    }
  }, [show]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!educationSystems?.length) return;
    if (
      educationSystemId &&
      educationSystems.some((s: any) => s.id === educationSystemId)
    ) {
      return;
    }
    setEducationSystemId(educationSystems[0].id);
  }, [show, hierarchySeeded, educationSystems, educationSystemId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!curricula?.length) {
      if (curriculumId) setCurriculumId("");
      return;
    }
    if (curriculumId && curricula.some((c: any) => c.id === curriculumId)) {
      return;
    }
    setCurriculumId(curricula[0].id);
  }, [show, hierarchySeeded, curricula, curriculumId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!courses?.length) return;
    if (courseId && courses.some((c: any) => c.id === courseId)) return;
    const preferred =
      courses.find((c: any) => c.id === defaultCourseId) || courses[0];
    setCourseId(preferred.id);
    form.setFieldValue("syncingToCourseId", preferred.id);
  }, [show, hierarchySeeded, courses, courseId, defaultCourseId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!stages.length) {
      if (stageId) setStageId("");
      return;
    }
    if (stageId && stages.some((s) => s.id === stageId)) return;
    const preferred =
      stages.find((s) => s.id === defaultStageId) ||
      stages.find((s) => s.grades?.some((g) => g.id === defaultGradeId)) ||
      stages.find((s) => s.grades?.length > 0) ||
      stages[0];
    setStageId(preferred.id);
  }, [
    show,
    hierarchySeeded,
    stages,
    stageId,
    defaultStageId,
    defaultGradeId,
  ]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!grades.length) {
      if (gradeId) {
        setGradeId("");
        form.setFieldValue("syncingToGradeId", "");
        clearTargetSubject();
      }
      return;
    }
    if (gradeId && grades.some((g) => g.id === gradeId)) return;
    const preferred =
      grades.find((g) => g.id === defaultGradeId) || grades[0];
    setGradeId(preferred.id);
    form.setFieldValue("syncingToGradeId", preferred.id);
    clearTargetSubject();
  }, [show, hierarchySeeded, grades, gradeId, defaultGradeId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    form.setFieldValue("syncingToCourseId", courseId || "");
  }, [courseId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    form.setFieldValue("syncingToGradeId", gradeId || "");
  }, [gradeId]);
  //#endregion

  return (
    <Modal
      id="syncSubject"
      isOpen={show}
      toggle={onClose}
      centered
      size="lg"
    >
      <ModalHeader className="p-3" toggle={onClose}>
        {getTitle()}
      </ModalHeader>

      {step === "select" && previewErrorMsg && (
        <Alert color="danger" className="rounded-0 mb-0">
          <p className="mb-0">{previewErrorMsg}</p>
        </Alert>
      )}

      {step === "confirm" && syncingErrorMsg && (
        <Alert color="danger" className="rounded-0 mb-0">
          <p className="mb-0">{syncingErrorMsg}</p>
        </Alert>
      )}

      {step === "select" && (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleShowDiff();
            return false;
          }}
          action="#"
        >
          <ModalBody>
            <Alert color="info" className="mb-3">
              <p className="mb-0">{t("syncSubjectHelp")}</p>
            </Alert>

            <Row className="g-2 mb-2">
              <Col md={4}>
                <Label className="form-label">
                  {t("educationSystem")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={educationSystemId}
                  onChange={(e) => {
                    setEducationSystemId(e.target.value);
                    setCurriculumId("");
                    setStageId("");
                    setGradeId("");
                    form.setFieldValue("syncingToGradeId", "");
                    clearTargetSubject();
                  }}
                >
                  {!educationSystems?.length ? (
                    <option value="">{t("selectEducationSystem")}</option>
                  ) : null}
                  {(educationSystems || []).map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {labelOf(s, langKey)}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={4}>
                <Label className="form-label">
                  {t("curriculumItem")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={curriculumId}
                  disabled={!educationSystemId}
                  onChange={(e) => {
                    setCurriculumId(e.target.value);
                    setStageId("");
                    setGradeId("");
                    form.setFieldValue("syncingToGradeId", "");
                    clearTargetSubject();
                  }}
                >
                  {!curricula?.length ? (
                    <option value="">{t("selectCurriculum")}</option>
                  ) : null}
                  {(curricula || []).map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {labelOf(c, langKey)}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={4}>
                <Label htmlFor="syncingToCourseId" className="form-label">
                  {t("syncToCourse")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="syncingToCourseId"
                  name="syncingToCourseId"
                  value={courseId}
                  onChange={(e) => {
                    const nextCourseId = e.target.value;
                    setCourseId(nextCourseId);
                    setStageId("");
                    setGradeId("");
                    form.setFieldValue("syncingToCourseId", nextCourseId);
                    form.setFieldValue("syncingToGradeId", "");
                    clearTargetSubject();
                  }}
                  onBlur={form.handleBlur}
                  invalid={
                    !!(
                      form.touched.syncingToCourseId &&
                      form.errors.syncingToCourseId
                    )
                  }
                >
                  {!courses?.length ? (
                    <option value="">{t("selectCourse")}</option>
                  ) : null}
                  {(courses || []).map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {labelOf(c, langKey)}
                    </option>
                  ))}
                </Input>
                {form.touched.syncingToCourseId &&
                  form.errors.syncingToCourseId && (
                    <FormFeedback type="invalid">
                      {form.errors.syncingToCourseId}
                    </FormFeedback>
                  )}
              </Col>
            </Row>

            <Row className="g-2 mb-2">
              <Col md={6}>
                <Label className="form-label">
                  {t("educationalStages")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={stageId}
                  disabled={!curriculumId || !courseId}
                  onChange={(e) => {
                    setStageId(e.target.value);
                    setGradeId("");
                    form.setFieldValue("syncingToGradeId", "");
                    clearTargetSubject();
                  }}
                >
                  {!stages.length ? (
                    <option value="">{t("selectEducationalStage")}</option>
                  ) : null}
                  {stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name[langKey] || stage.name.en}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={6}>
                <Label htmlFor="syncingToGradeId" className="form-label">
                  {t("syncToGrade")}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="syncingToGradeId"
                  name="syncingToGradeId"
                  value={gradeId}
                  disabled={!stageId}
                  onChange={(e) => {
                    const nextGradeId = e.target.value;
                    setGradeId(nextGradeId);
                    form.setFieldValue("syncingToGradeId", nextGradeId);
                    form.setFieldTouched("syncingToGradeId", true);
                    clearTargetSubject();
                  }}
                  onBlur={form.handleBlur}
                  invalid={
                    !!(
                      form.touched.syncingToGradeId &&
                      form.errors.syncingToGradeId
                    )
                  }
                >
                  {!grades.length ? (
                    <option value="">{t("selectGrade")}</option>
                  ) : null}
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name[langKey] || grade.name.en}
                    </option>
                  ))}
                </Input>
                {form.touched.syncingToGradeId &&
                  form.errors.syncingToGradeId && (
                    <FormFeedback type="invalid">
                      {form.errors.syncingToGradeId}
                    </FormFeedback>
                  )}
              </Col>
            </Row>

            <div className="mb-3">
              <Label htmlFor="targetSubjectId" className="form-label">
                {t("syncToSubject")}
                <span className="text-danger">*</span>
              </Label>
              <SubjectSelect
                gradeId={gradeId || form.values.syncingToGradeId}
                excludeSubjectId={id}
                type="select"
                id="targetSubjectId"
                name="targetSubjectId"
                className="form-select"
                aria-label={t("selectSubject")}
                placeholder={t("selectSubject")}
                value={form.values.targetSubjectId}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.targetSubjectId && !!form.errors.targetSubjectId
                }
              />
              {form.touched.targetSubjectId && form.errors.targetSubjectId && (
                <FormFeedback type="invalid">
                  {form.errors.targetSubjectId}
                </FormFeedback>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to="#"
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title={t("syncShowDiff")}
              inProgressTitle="loading"
              color="primary"
              className="btn btn-primary"
              type="submit"
              inProgress={previewLoading}
              disabled={previewLoading}
            />
          </ModalFooter>
        </Form>
      )}

      {step === "diff" && preview && (
        <>
          <ModalBody>
            <SyncDiffSummary preview={preview} localizedName={localizedName} />
            <SyncDiffTable
              preview={preview}
              entityTypeLabel={entityTypeLabel}
              actionLabel={actionLabel}
            />
          </ModalBody>
          <ModalFooter>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                setStep("select");
                setPreview(null);
              }}
            >
              {t("syncBack")}
            </button>
            <Link
              to="#"
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!hasChanges}
              onClick={() => setStep("confirm")}
            >
              {t("syncContinueToConfirm")}
            </button>
          </ModalFooter>
        </>
      )}

      {step === "confirm" && preview && (
        <>
          <ModalBody>
            <div className="text-center py-2">
              <i
                className="ri-error-warning-line text-warning"
                style={{ fontSize: "3rem" }}
              />
              <h5 className="mt-3">{t("syncConfirmTitle")}</h5>
              <p className="text-muted mb-3">{t("syncConfirmPrompt")}</p>
            </div>
            <SyncDiffSummary preview={preview} localizedName={localizedName} />
            <Alert color="warning" className="mb-0">
              <p className="mb-0">{t("syncExecuteConfirm")}</p>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setStep("diff")}
              disabled={syncingSubject}
            >
              {t("syncBackToDiff")}
            </button>
            <Link
              to="#"
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title={t("syncExecute")}
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="button"
              onClick={handleExecuteSync}
              inProgress={syncingSubject}
              disabled={syncingSubject || !hasChanges}
            />
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};
